import { useState } from 'react'
import { MdClose, MdPlayArrow } from 'react-icons/md'
import type { CatalogMedia } from '@/lib/api/catalog/catalog-api'

interface CatalogMediaGalleryProps {
    media: CatalogMedia[]
}

/**
 * Galeria somente-leitura de um item do catálogo, para a tela de detalhes.
 *
 * Mostra a capa grande e as demais como miniaturas; clicar abre em tela cheia.
 * O vídeo só vira player DEPOIS do clique — uma página com quatro `<iframe>` do
 * YouTube carrega o player do YouTube quatro vezes, e a tela de detalhes já é
 * pesada.
 */
export default function CatalogMediaGallery({ media }: CatalogMediaGalleryProps) {
    const [selected, setSelected] = useState<CatalogMedia | null>(null)

    if (!media.length) return null

    const [cover, ...rest] = media

    return (
        <>
            <div className="card bg-base-100 border border-base-300/50">
                <div className="card-body">
                    <h2 className="card-title text-base">Fotos e vídeos</h2>

                    <button
                        type="button"
                        onClick={() => setSelected(cover)}
                        className="relative mt-2 aspect-video w-full overflow-hidden rounded-lg bg-base-200"
                    >
                        <MediaPreview item={cover} />
                    </button>

                    {rest.length > 0 && (
                        <div className="mt-3 grid grid-cols-4 gap-2 sm:grid-cols-6">
                            {rest.map(item => (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => setSelected(item)}
                                    className="relative aspect-square overflow-hidden rounded-md bg-base-200"
                                >
                                    <MediaPreview item={item} />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {selected && (
                <div
                    className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 p-4"
                    onClick={() => setSelected(null)}
                >
                    <button
                        type="button"
                        className="btn btn-circle btn-sm absolute right-4 top-4"
                        onClick={() => setSelected(null)}
                        aria-label="Fechar"
                    >
                        <MdClose />
                    </button>

                    <div
                        className="max-h-full w-full max-w-4xl"
                        onClick={e => e.stopPropagation()}
                    >
                        {selected.kind === 'IMAGE' && selected.url ? (
                            <img
                                src={selected.url}
                                alt={selected.title ?? ''}
                                className="mx-auto max-h-[85vh] rounded-lg object-contain"
                            />
                        ) : selected.provider === 'YOUTUBE' && selected.videoId ? (
                            <iframe
                                src={`https://www.youtube.com/embed/${selected.videoId}?autoplay=1`}
                                className="aspect-video w-full rounded-lg"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                title={selected.title ?? 'Vídeo'}
                            />
                        ) : selected.url ? (
                            <video
                                src={selected.url}
                                poster={selected.poster ?? undefined}
                                controls
                                autoPlay
                                className="max-h-[85vh] w-full rounded-lg"
                            />
                        ) : null}

                        {selected.title && (
                            <p className="mt-3 text-center text-sm text-white/80">
                                {selected.title}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </>
    )
}

function MediaPreview({ item }: { item: CatalogMedia }) {
    if (item.kind === 'IMAGE' && item.url) {
        return (
            <img
                src={item.url}
                alt={item.title ?? ''}
                className="h-full w-full object-cover"
                loading="lazy"
            />
        )
    }

    const thumbnail =
        item.provider === 'YOUTUBE' && item.videoId
            ? `https://img.youtube.com/vi/${item.videoId}/hqdefault.jpg`
            : item.poster

    return (
        <>
            {thumbnail ? (
                <img
                    src={thumbnail}
                    alt={item.title ?? 'Vídeo'}
                    className="h-full w-full object-cover"
                    loading="lazy"
                />
            ) : (
                <span className="flex h-full w-full items-center justify-center bg-base-300" />
            )}
            <span className="absolute inset-0 flex items-center justify-center bg-black/25">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90">
                    <MdPlayArrow className="text-2xl text-base-content" />
                </span>
            </span>
        </>
    )
}
